<script setup lang="ts">
import useLocale from '../../composables/useLocale';
import HighlightColorPickerButton from './HighlightColorPickerButton.vue';
import type { HighlightWithText } from '../../stores/highlights';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUnderline } from '@fortawesome/pro-regular-svg-icons';

library.add(faUnderline);

type ColorOption = { name: string; light: string; dark: string };

type Emits = {
  (e: 'selectColor', colorName: string): void;
  (e: 'selectUnderline'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  displayColors: ColorOption[];
  isDarkTheme: boolean;
  activeAnnotation: HighlightWithText | null;
};
withDefaults(defineProps<Props>(), {
  displayColors: () => [],
  isDarkTheme: false,
  activeAnnotation: null,
});

const { t } = useLocale();
</script>

<template>
  <div
    role="group"
    :aria-label="t('book_player.label_highlight_toolbar_title')"
    class="d-flex align-center justify-center flex-wrap ga-1"
  >
    <HighlightColorPickerButton
      v-for="color in displayColors"
      :key="`color-${color.light}`"
      :title="color.name"
      :color="isDarkTheme ? color.dark : color.light"
      :active="activeAnnotation?.type !== 'underline' && activeAnnotation?.color === color.name"
      @click="emit('selectColor', color.name)"
    />
    <v-btn
      color="on-primary"
      variant="text"
      :ripple="false"
      icon
      :class="{ 'underline-is-active': activeAnnotation?.type === 'underline' }"
      :title="t('book_player.label_annotation_underline')"
      :aria-label="t('book_player.label_annotation_underline')"
      :aria-pressed="activeAnnotation?.type === 'underline'"
      @click="emit('selectUnderline')"
    >
      <div class="underline-ball" :class="{ active: activeAnnotation?.type === 'underline' }">
        <FontAwesomeIcon icon="far fa-underline" size="sm" />
      </div>
      <v-tooltip activator="parent" location="bottom">
        {{ t('book_player.label_annotation_underline') }}
      </v-tooltip>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
.underline-is-active {
  overflow: visible !important;
}

.underline-ball {
  width: 30px;
  height: 30px;
  background-color: rgb(var(--v-theme-error));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition:
    outline 0.12s ease,
    transform 0.12s ease;

  &:hover:not(.active) {
    transform: scale(1.1);
  }

  &.active {
    outline: 3px solid var(--highlight-ring-color, rgb(var(--v-theme-on-inverse-surface)));
    outline-offset: 2px;
  }
}
</style>
